/**
 * Revit Bridge - gRPC Communication Layer
 *
 * This module provides the bridge between the React UI and the Revit add-in.
 * All Web UI <-> Revit communication goes through gRPC; WebView2 is only a
 * shell that hosts the Web UI. There is no COM data channel.
 *
 * When the gRPC runtime is unavailable, functions fall back to mock data for
 * development purposes.
 */

import {
  RevitParameter,
  RevitElement,
  RevitSettings,
  RevitApiResponse,
  RevitCommandRequest,
  RevitCommandResponse,
  RevitCommandName,
  RevitQueryOptions,
  RevitFamily,
  RevitDataType,
  RevitAvailableParameter,
  type RevitElementCategory,
} from './types';
import { normalizeRevitDataType } from './revitDataType';

import {
  MOCK_REVIT_PARAMETERS,
  MOCK_REVIT_ELEMENTS,
  MOCK_REVIT_SETTINGS,
  MOCK_REVIT_FAMILIES,
} from './mockData';

import { isGrpcRuntimeAvailable } from '../grpc/transport';
import type {
  PendingRevitOperationRecoveryMetadata,
  RevitOperationProgressSnapshot,
} from '../grpc/operationProgressClient';
import {
  getFamiliesWithParametersGrpc,
  getAvailableParametersGrpc,
  loadFamilyIntoProjectGrpc,
  addParametersToSpecificFamiliesGrpc,
  undoAddedParametersToSpecificFamiliesGrpc,
  updateElementParametersGrpc,
  updateFamilyTypeParameterGrpc,
  updateFamilyTypeParametersGrpc,
} from '../grpc/familyClient';
import {
  getSharedParametersGrpc,
  loadSharedParameterFileGrpc,
  createTypeParametersGrpc,
  addParametersToAllFamiliesGrpc,
  addProjectParametersGrpc,
  deleteAndReAddParameterGrpc,
  createParametersFromParsedFileGrpc,
  undoAddedProjectParametersGrpc,
} from '../grpc/sharedParameterClient';
import { highlightElementsGrpc } from '../grpc/validationGrpcClient';
import { browseForFolderGrpc } from '../grpc/revitClient';
import type { ConnectorParameterUndoEntry, ConnectorProjectParameterUndoEntry } from '../../types/parameterSetupUndo';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Configuration for the Revit Bridge
 */
interface RevitBridgeConfig {
  /** Simulated network delay in milliseconds (for development) */
  mockDelay: number;
  /** Whether to log bridge operations to console */
  enableLogging: boolean;
  /** Whether to simulate random failures (for testing error handling) */
  simulateErrors: boolean;
  /** Error simulation rate (0-1) */
  errorRate: number;
}

const defaultConfig: RevitBridgeConfig = {
  mockDelay: 200,
  enableLogging: false,
  simulateErrors: false,
  errorRate: 0.1,
};

let config: RevitBridgeConfig = { ...defaultConfig };

/**
 * Configure the Revit Bridge
 */
export function configureRevitBridge(newConfig: Partial<RevitBridgeConfig>): void {
  config = { ...config, ...newConfig };
}

// =============================================================================
// Helpers
// =============================================================================

export interface BrowseFolderResult {
  success: boolean;
  path?: string;
  canceled?: boolean;
  error?: string;
}

export interface ExpectedRevitDocumentIdentity {
  expectedDocumentName?: string;
  expectedDocumentPath?: string;
}

export interface ParameterAdditionPayload extends ExpectedRevitDocumentIdentity {
  parameterNames: string[];
  categories?: string[];
  sharedParameterFilePath?: string;
  sharedParameterFileContent?: string;
  bindingKind?: 'type' | 'instance';
  targetObjectTypes?: string[]; objectTypeParameterName?: string;
  requireReversibleMutations?: boolean;
  parameterTypeHints?: Array<{ name: string; dataType: string }>;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export interface UndoAddedFamilyParameterEntry {
  familyId: string;
  familyName?: string;
  parameterName: string;
  existedBefore: boolean;
  previousBindingKind?: 'type' | 'instance';
}

export interface UndoAddedParametersToSpecificFamiliesPayload extends ExpectedRevitDocumentIdentity {
  entries: UndoAddedFamilyParameterEntry[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export interface UndoAddedProjectParametersPayload extends ExpectedRevitDocumentIdentity {
  parameterNames: string[]; entries?: ConnectorProjectParameterUndoEntry[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export interface GetFamiliesOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  scopedFamilyIds?: string[];
}

export interface GetAvailableParametersOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export async function browseForFolder(initialPath = ''): Promise<BrowseFolderResult> {
  log('browseForFolder called', { initialPath });
  // gRPC folder picker (RevitService.BrowseForFolder). Returns the same
  // { success, path?, canceled?, error? } shape as BrowseFolderResult.
  return browseForFolderGrpc(initialPath);
}

/**
 * Helper to simulate async delay for mock data
 */
async function simulateDelay(): Promise<void> {
  if (config.mockDelay > 0) {
    await new Promise((resolve) => setTimeout(resolve, config.mockDelay));
  }
}

/**
 * Helper to simulate random errors for testing
 */
function shouldSimulateError(): boolean {
  return config.simulateErrors && Math.random() < config.errorRate;
}

/**
 * Logger helper
 */
function log(message: string, data?: unknown): void {
  if (config.enableLogging) {
    console.log(`[RevitBridge] ${message}`, data ?? '');
  }
}

// =============================================================================
// Parameter Operations
// =============================================================================

/**
 * Get all available Revit parameters
 *
 * In production, this would call the Revit API through WebView2 to get
 * the actual shared parameters defined in the Revit project.
 *
 * @returns Promise resolving to array of RevitParameter objects
 */
export async function getParameters(): Promise<RevitApiResponse<RevitParameter[]>> {
  log('getParameters called');

  // Fallback to mock data for development
  await simulateDelay();

  if (shouldSimulateError()) {
    return {
      success: false,
      error: 'Simulated error: Failed to retrieve parameters from Revit',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    success: true,
    data: MOCK_REVIT_PARAMETERS,
    message: 'Mock data - parameters retrieved successfully',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get parameters filtered by category
 */
export async function getParametersByCategory(
  category: string
): Promise<RevitApiResponse<RevitParameter[]>> {
  log('getParametersByCategory called', { category });

  await simulateDelay();

  const filteredParams = MOCK_REVIT_PARAMETERS.filter((p) => p.category === category);

  return {
    success: true,
    data: filteredParams,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Update a parameter value on selected elements
 *
 * In production, this would call the Revit API to update parameter values
 * on the currently selected elements in Revit.
 */
export async function updateParameterValue(
  parameterName: string,
  value: string | number | boolean,
  elementIds?: string[]
): Promise<RevitApiResponse<boolean>> {
  log('updateParameterValue called', { parameterName, value, elementIds });

  if (isGrpcRuntimeAvailable() && Array.isArray(elementIds) && elementIds.length > 0) {
    const normalizedUpdates = elementIds
      .map((elementId) => String(elementId ?? '').trim())
      .filter(Boolean)
      .map((elementId) => ({
        elementId,
        parameterName,
        value: String(value ?? ''),
      }));

    if (normalizedUpdates.length > 0) {
      const grpcResult = await updateElementParametersGrpc(normalizedUpdates);
      return {
        success: grpcResult.success,
        data: grpcResult.success,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Fallback to mock data for development
  await simulateDelay();

  if (shouldSimulateError()) {
    return {
      success: false,
      error: 'Simulated error: Failed to update parameter value',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    success: true,
    data: true,
    message: `Mock: Parameter ${parameterName} updated to ${value}`,
    timestamp: new Date().toISOString(),
  };
}

export interface ElementParameterBatchUpdate {
  elementId: string;
  parameterName: string;
  value: string;
}

export async function updateElementParameters(
  updates: ElementParameterBatchUpdate[]
): Promise<RevitApiResponse<{ totalUpdates: number; successCount: number; errorCount: number; errors?: string[] }>> {
  log('updateElementParameters called', { updates: updates.length });

  const normalizedUpdates = Array.from(
    new Map(
      updates
        .filter(
          (update) =>
            update &&
            typeof update.elementId === 'string' &&
            update.elementId.trim() !== '' &&
            typeof update.parameterName === 'string' &&
            update.parameterName.trim() !== ''
        )
        .map((update) => [
          `${update.elementId}||${update.parameterName}`.toLowerCase(),
          {
            elementId: update.elementId.trim(),
            parameterName: update.parameterName.trim(),
            value: update.value ?? '',
          },
        ])
    ).values()
  );

  if (normalizedUpdates.length === 0) {
    return {
      success: false,
      error: 'No element updates provided',
      timestamp: new Date().toISOString(),
    };
  }

  if (isGrpcRuntimeAvailable()) {
    const grpcResult = await updateElementParametersGrpc(normalizedUpdates);
    return {
      success: grpcResult.success,
      data: grpcResult.success
        ? {
            totalUpdates: grpcResult.totalUpdates || normalizedUpdates.length,
            successCount: grpcResult.successCount || 0,
            errorCount: grpcResult.errorCount || 0,
            ...(grpcResult.errors ? { errors: grpcResult.errors } : {}),
          }
        : undefined,
      error: grpcResult.error,
      timestamp: new Date().toISOString(),
    };
  }

  const groupedUpdates = Array.from(
    normalizedUpdates.reduce((groups, update) => {
      const key = `${update.parameterName}||${update.value}`;
      const bucket = groups.get(key) ?? [];
      bucket.push(update.elementId);
      groups.set(key, bucket);
      return groups;
    }, new Map<string, string[]>())
  );

  const errors: string[] = [];
  let successCount = 0;

  for (const [groupKey, elementIds] of groupedUpdates) {
    const [parameterName, value] = groupKey.split('||');
    const result = await updateParameterValue(parameterName, value, elementIds);
    if (!result.success) {
      errors.push(result.error || `Failed to update ${parameterName}`);
      continue;
    }

    successCount += elementIds.length;
  }

  return {
    success: errors.length === 0,
    data: {
      totalUpdates: normalizedUpdates.length,
      successCount,
      errorCount: normalizedUpdates.length - successCount,
      ...(errors.length > 0 ? { errors } : {}),
    },
    ...(errors.length > 0 ? { error: errors[0] } : {}),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Update a parameter value on a family type
 * Used when editing values in the QuickFill table
 *
 * @param familyTypeName Format: "FamilyName:TypeName" e.g., "Cross - Generic:Standard"
 * @param parameterName The parameter name to update
 * @param value The new value
 */
export async function updateFamilyTypeParameter(
  familyTypeName: string,
  parameterName: string,
  value: string
): Promise<RevitApiResponse<{ familyType: string; parameter: string; newValue: string }>> {
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await updateFamilyTypeParameterGrpc(familyTypeName, parameterName, value);
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          familyType: grpcResult.familyType || familyTypeName,
          parameter: grpcResult.parameter || parameterName,
          newValue: grpcResult.newValue || value,
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('[updateFamilyTypeParameter] gRPC error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'gRPC error',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Fallback to mock for development
  await simulateDelay();

  return {
    success: true,
    data: {
      familyType: familyTypeName,
      parameter: parameterName,
      newValue: value,
    },
    message: `Mock: Updated ${parameterName} on ${familyTypeName} to "${value}"`,
    timestamp: new Date().toISOString(),
  };
}

export interface FamilyTypeParameterBatchUpdate {
  FamilyType: string;
  ParameterName: string;
  Value: string;
}

export async function updateFamilyTypeParameters(
  updates: FamilyTypeParameterBatchUpdate[]
): Promise<RevitApiResponse<{ totalUpdates: number; successCount: number; errorCount: number; errors?: string[] }>> {
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcUpdates = (updates ?? []).map((u) => ({
        familyType: u.FamilyType,
        parameterName: u.ParameterName,
        value: u.Value,
      }));
      const grpcResult = await updateFamilyTypeParametersGrpc(grpcUpdates);
      return {
        success: grpcResult.success,
        data: {
          totalUpdates: grpcResult.totalUpdates || updates.length,
          successCount: grpcResult.successCount || 0,
          errorCount: grpcResult.errorCount || 0,
          errors: grpcResult.errors,
        },
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('[updateFamilyTypeParameters] gRPC error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'gRPC error',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Fallback to mock for development
  await simulateDelay();
  return {
    success: true,
    data: {
      totalUpdates: updates.length,
      successCount: updates.length,
      errorCount: 0,
      errors: [],
    },
    message: `Mock: Updated ${updates.length} parameters`,
    timestamp: new Date().toISOString(),
  };
}

// =============================================================================
// Element Operations
// =============================================================================

/**
 * Get Revit elements with optional filtering
 *
 * In production, this would query Revit for elements matching the filter criteria.
 */
export async function getElements(
  options?: RevitQueryOptions
): Promise<RevitApiResponse<RevitElement[]>> {
  log('getElements called', options);

  // Fallback to mock data for development
  await simulateDelay();

  if (shouldSimulateError()) {
    return {
      success: false,
      error: 'Simulated error: Failed to retrieve elements from Revit',
      timestamp: new Date().toISOString(),
    };
  }

  let elements = [...MOCK_REVIT_ELEMENTS];

  if (Array.isArray(options?.parameterNames) && options.parameterNames.length > 0) {
    const requested = new Set(options.parameterNames);
    elements = elements.map((element) => ({
      ...element,
      parameterValues: Array.isArray(element.parameterValues)
        ? element.parameterValues.filter((parameterValue) => requested.has(parameterValue.parameterName))
        : element.parameterValues,
    }));
  }

  // Apply filters if provided
  if (options?.filters) {
    const { category, familyName, selectedOnly } = options.filters;

    if (category) {
      const categories = Array.isArray(category) ? category : [category];
      elements = elements.filter((e) => e.category && categories.includes(e.category));
    }

    if (familyName) {
      elements = elements.filter((e) => e.familyName === familyName);
    }

    if (selectedOnly) {
      elements = elements.filter((e) => e.isSelected);
    }
  }

  // Apply pagination if provided
  if (options?.limit) {
    const offset = options.offset ?? 0;
    elements = elements.slice(offset, offset + options.limit);
  }

  return {
    success: true,
    data: elements,
    message: 'Mock data - elements retrieved successfully',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get currently selected elements in Revit
 */
export async function getSelectedElements(): Promise<RevitApiResponse<RevitElement[]>> {
  log('getSelectedElements called');

  // Fallback to mock data for development
  await simulateDelay();

  // Return first two elements as "selected" for mock purposes
  const selectedElements = MOCK_REVIT_ELEMENTS.slice(0, 2).map((e) => ({
    ...e,
    isSelected: true,
  }));

  return {
    success: true,
    data: selectedElements,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Select elements in Revit by their IDs
 */
export async function selectElements(elementIds: string[]): Promise<RevitApiResponse<boolean>> {
  log('selectElements called', { elementIds });

  const normalizedIds = Array.from(
    new Set(
      elementIds
        .map((id) => String(id ?? '').trim())
        .filter((id) => /^-?\d+$/.test(id))
        .filter((id) => {
          try {
            return BigInt(id) > 0n;
          } catch {
            return false;
          }
        })
    )
  );

  if (normalizedIds.length === 0) {
    return {
      success: false,
      data: false,
      error: 'No valid element IDs provided',
      timestamp: new Date().toISOString(),
    };
  }

  if (isGrpcRuntimeAvailable()) {
    try {
      const result = await highlightElementsGrpc(normalizedIds.map((id) => BigInt(id)));
      return {
        success: result.success,
        data: result.success,
        error: result.error,
        message: result.success
          ? `Selected ${result.count ?? normalizedIds.length} element(s) in Revit`
          : result.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to select elements in Revit',
        timestamp: new Date().toISOString(),
      };
    }
  }

  await simulateDelay();

  return {
    success: true,
    data: true,
    message: `Mock: Selected ${normalizedIds.length} elements`,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Highlight elements in Revit (temporary visual highlight)
 */
export async function highlightElements(elementIds: string[]): Promise<RevitApiResponse<boolean>> {
  log('highlightElements called', { elementIds });

  await simulateDelay();

  return {
    success: true,
    data: true,
    message: `Mock: Highlighted ${elementIds.length} elements`,
    timestamp: new Date().toISOString(),
  };
}

// =============================================================================
// Family Operations
// =============================================================================

function mapGrpcFamiliesToRevitFamilies(
  families: Array<{
    id: string;
    name: string;
    category: string;
    isEditable: boolean;
    isSystemFamily: boolean;
    types: Array<{
      id: string;
      name: string;
      parameterValues: Array<{
        parameterId: string;
        parameterName: string;
        value: string;
        isReadOnly: boolean;
        isInstance: boolean;
        dataType: string;
      }>;
    }>;
  }>
): RevitFamily[] {
  return families.map((f) => ({
    id: f.id,
    name: f.name,
    category: f.category as RevitElementCategory,
    isEditable: f.isEditable,
    isSystemFamily: f.isSystemFamily,
    types: f.types.map((t) => ({
      id: t.id,
      name: t.name,
      parameterValues: t.parameterValues.map((pv) => ({
        parameterId: pv.parameterId,
        parameterName: pv.parameterName,
        value: pv.value,
        isReadOnly: pv.isReadOnly,
        isInstance: pv.isInstance,
        dataType: normalizeRevitDataType(pv.dataType),
      })),
    })),
    parameters: [],
  }));
}

/**
 * Get all families in the Revit project
 * @param parameterNames - Optional array of specific parameter names to fetch. If not provided, fetches ALL parameters.
 */
export async function getFamilies(
  parameterNames?: string[],
  options: GetFamiliesOptions = {}
): Promise<RevitApiResponse<RevitFamily[]>> {
  log('getFamilies called', {
    filtered: Array.isArray(parameterNames) && parameterNames.length > 0,
    parameterCount: parameterNames?.length ?? 0,
    progressRequested: Boolean(options.onProgress),
    scopedFamilyCount: options.scopedFamilyIds?.length ?? 0,
  });

  if (options.onProgress && isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await getFamiliesWithParametersGrpc(parameterNames, {
        onProgress: options.onProgress,
        scopedFamilyIds: options.scopedFamilyIds,
      });
      if (grpcResult.success && grpcResult.families) {
        return {
          success: true,
          data: mapGrpcFamiliesToRevitFamilies(grpcResult.families),
          timestamp: new Date().toISOString(),
        };
      }

      log('gRPC getFamilies async progress path returned error', grpcResult.error);
      return {
        success: false,
        error: grpcResult.error || 'Family snapshot request failed',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC progress path failed for getFamilies', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Family snapshot request failed',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await getFamiliesWithParametersGrpc(parameterNames, {
        scopedFamilyIds: options.scopedFamilyIds,
      });
      if (grpcResult.success && grpcResult.families) {
        return {
          success: true,
          data: mapGrpcFamiliesToRevitFamilies(grpcResult.families),
          timestamp: new Date().toISOString(),
        };
      }
      // gRPC failed — fall through to mock data instead of returning error
      log('gRPC getFamilies returned error, falling through to mock data', grpcResult.error);
    } catch (error) {
      log('gRPC fallback failed for getFamilies', error);
    }
  }

  log('getFamilies: gRPC unavailable or failed, using mock data');
  // Fallback to mock data for development
  await simulateDelay();

  return {
    success: true,
    data: MOCK_REVIT_FAMILIES,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get all available parameters for pipe-related categories
 * Returns both TYPE and INSTANCE parameters that exist on elements in Pipes, Pipe Fittings, Pipe Accessories, Generic Models, and Mechanical Equipment
 */
export async function getAvailableParameters(
  options: GetAvailableParametersOptions = {}
): Promise<RevitApiResponse<RevitAvailableParameter[]>> {
  log('getAvailableParameters called', {
    progressRequested: Boolean(options.onProgress),
  });

  // gRPC is the only live transport; there is no COM bridge.
  if (!isGrpcRuntimeAvailable()) {
    log('getAvailableParameters: gRPC transport unavailable');
    return {
      success: false,
      error: 'The Revit connection is unavailable. Open PipeEX inside Revit.',
      timestamp: new Date().toISOString(),
    };
  }

  try {
    const grpcResult = options.onProgress
      ? await getAvailableParametersGrpc({ onProgress: options.onProgress })
      : await getAvailableParametersGrpc();

    if (grpcResult.success && grpcResult.parameters) {
      return {
        success: true,
        data: grpcResult.parameters.map((parameter) => ({
          name: parameter.name, label: parameter.label, group: parameter.group, dataType: parameter.dataType,
          parameterType: parameter.parameterType as 'Type' | 'Instance',
          isProjectBinding: parameter.isProjectBinding, categories: parameter.categories,
          source: parameter.source,
        })),
        timestamp: new Date().toISOString(),
      };
    }

    return {
      success: false,
      error: grpcResult.error || 'Failed to get available parameters',
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    log('getAvailableParameters gRPC call threw', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      timestamp: new Date().toISOString(),
    };
  }
}

// =============================================================================
// Shared Parameter Operations
// =============================================================================

/**
 * Shared parameter data structure
 */
export interface SharedParameter {
  name: string;
  guid: string;
  group: string;
  dataType: string;
  description: string;
  visible: boolean;
}

/**
 * Get all parameters from the shared parameter file
 * This allows users to see ALL available parameters they can select from
 */
export async function getSharedParameters(): Promise<RevitApiResponse<SharedParameter[]>> {
  log('getSharedParameters called');

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await getSharedParametersGrpc();
      if (grpcResult.success && grpcResult.parameters) {
        return {
          success: true,
          data: grpcResult.parameters,
          timestamp: new Date().toISOString(),
        };
      }
      log('gRPC getSharedParameters returned error, falling through', grpcResult.error);
    } catch (error) {
      log('gRPC fallback failed for getSharedParameters', error);
    }
  }

  log('gRPC unavailable or failed, returning empty shared parameters');
  // Fallback - return empty array for development
  return {
    success: true,
    data: [],
    timestamp: new Date().toISOString(),
  };
}

// =============================================================================
// Settings Operations
// =============================================================================

/**
 * Get current Revit project settings
 *
 * In production, this would retrieve actual project settings from Revit.
 */
export async function getSettings(): Promise<RevitApiResponse<RevitSettings>> {
  log('getSettings called');

  await simulateDelay();

  if (shouldSimulateError()) {
    return {
      success: false,
      error: 'Simulated error: Failed to retrieve settings from Revit',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    success: true,
    data: {
      ...MOCK_REVIT_SETTINGS,
      lastUpdated: new Date().toISOString(),
    },
    message: 'Mock data - settings retrieved successfully',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Update Revit project settings
 */
export async function updateSettings(
  settings: Partial<RevitSettings>
): Promise<RevitApiResponse<RevitSettings>> {
  log('updateSettings called', settings);

  await simulateDelay();

  const updatedSettings = {
    ...MOCK_REVIT_SETTINGS,
    ...settings,
    lastUpdated: new Date().toISOString(),
  };

  return {
    success: true,
    data: updatedSettings,
    message: 'Mock: Settings updated successfully',
    timestamp: new Date().toISOString(),
  };
}

// =============================================================================
// Command Execution
// =============================================================================

/**
 * Execute a Revit command
 *
 * This is the generic command execution interface. Specific commands
 * like ExportPCF, ValidateParameters, etc. can be executed through this method.
 *
 * In production, this would trigger the corresponding External Command
 * in the Revit add-in.
 */
export async function executeCommand<T = unknown>(
  request: RevitCommandRequest
): Promise<RevitCommandResponse<T>> {
  log('executeCommand called', request);

  const startTime = Date.now();

  await simulateDelay();

  if (shouldSimulateError()) {
    return {
      success: false,
      commandName: request.commandName,
      error: `Simulated error: Failed to execute command ${request.commandName}`,
      duration: Date.now() - startTime,
    };
  }

  // Mock command execution results
  const mockResults: Record<RevitCommandName, unknown> = {
    RefreshParameters: { refreshed: true, count: MOCK_REVIT_PARAMETERS.length },
    UpdateParameter: { updated: true },
    SelectElements: { selected: true, count: 0 },
    HighlightElements: { highlighted: true },
    ExportPCF: { exported: true, filePath: 'C:\\Export\\output.pcf' },
    ImportConfiguration: { imported: true },
    ExportConfiguration: { exported: true, filePath: 'C:\\Export\\config.json' },
    SyncWithCloud: { synced: true },
    ValidateParameters: { valid: true, errors: [] },
    ApplyMappings: { applied: true, mappingsCount: 10 },
  };

  return {
    success: true,
    commandName: request.commandName,
    result: mockResults[request.commandName] as T,
    duration: Date.now() - startTime,
  };
}

// =============================================================================
// Convenience Command Functions
// =============================================================================

/**
 * Refresh all parameters from Revit
 */
export async function refreshParameters(): Promise<RevitCommandResponse<{ count: number }>> {
  return executeCommand<{ count: number }>({
    commandName: 'RefreshParameters',
  });
}

/**
 * Export PCF file
 */
export async function exportPCF(options?: {
  outputPath?: string;
  selectedOnly?: boolean;
}): Promise<RevitCommandResponse<{ filePath: string }>> {
  return executeCommand<{ filePath: string }>({
    commandName: 'ExportPCF',
    parameters: options,
  });
}

/**
 * Validate parameter mappings
 */
export async function validateParameters(): Promise<
  RevitCommandResponse<{ valid: boolean; errors: string[] }>
> {
  return executeCommand<{ valid: boolean; errors: string[] }>({
    commandName: 'ValidateParameters',
  });
}

/**
 * Apply mapping configuration to elements
 */
export async function applyMappings(
  mappingConfig: Record<string, unknown>
): Promise<RevitCommandResponse<{ applied: boolean }>> {
  return executeCommand<{ applied: boolean }>({
    commandName: 'ApplyMappings',
    parameters: mappingConfig,
  });
}

// =============================================================================
// Shared Parameter Management
// =============================================================================

/**
 * Shared parameter file information
 */
export interface SharedParameterFileInfo {
  filePath: string;
  parameterCount: number;
  parameters: SharedParameterDefinition[];
}

/**
 * Shared parameter definition
 */
export interface SharedParameterDefinition {
  name: string;
  guid: string;
  group: string;
  dataType: RevitDataType;
  parameterType: string;
  description: string;
  visible: boolean;
}

/**
 * Parameter creation result
 */
export interface ParameterCreationResult {
  parameter: string;
  status?: 'Created' | 'Updated' | 'AlreadyExistsCompatible' | 'Conflict' | 'Failed';
  success: boolean;
  message?: string;
  error?: string;
  guid?: string;
  categories?: string[];
}

/**
 * Family with parameters information
 */
export interface FamilyWithParameters {
  id: string;
  name: string;
  category: string;
  isEditable: boolean;
  types: FamilyTypeWithParameters[];
}

/**
 * Family type with parameters
 */
export interface FamilyTypeWithParameters {
  id: string;
  name: string;
  parameters: FamilyParameterInfo[];
}

/**
 * Family parameter information
 */
export interface FamilyParameterInfo {
  name: string;
  value: string | number | boolean;
  dataType: RevitDataType;
  isShared: boolean;
  isInstance: boolean;
}

/**
 * Load shared parameters from a file
 *
 * @param filePath Path to shared parameter file. If empty, uses Revit's default.
 * @returns Promise resolving to shared parameter file information
 */
export async function loadSharedParameterFile(
  filePath?: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<RevitApiResponse<SharedParameterFileInfo>> {
  log('loadSharedParameterFile called', { filePath });

  if (isGrpcRuntimeAvailable() && options?.onProgress) {
    try {
      const grpcResult = await loadSharedParameterFileGrpc(filePath, options);
      return {
        success: grpcResult.success,
        data: grpcResult.success
          ? {
              filePath: grpcResult.filePath ?? filePath ?? '',
              parameterCount: grpcResult.parameterCount ?? grpcResult.parameters?.length ?? 0,
              parameters: (grpcResult.parameters ?? []).map(parameter => ({
                ...parameter,
                dataType: normalizeRevitDataType(parameter.dataType),
              })),
            }
          : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC progress path failed for loadSharedParameterFile', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Shared parameter file operations not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Create TYPE parameters from shared parameter file
 *
 * @param parameterNames Array of parameter names to create
 * @param categories Array of category names (PIPES, PIPE FITTINGS, PIPE ACCESSORIES)
 * @param sharedParamFile Optional path to shared parameter file
 * @returns Promise resolving to creation results
 */
export async function createTypeParameters(
  parameterNames: string[],
  categories: string[],
  sharedParamFile?: string,
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<
  RevitApiResponse<{
    successCount: number;
    failureCount: number;
    totalCount: number;
    results: ParameterCreationResult[];
  }>
> {
  log('createTypeParameters called', { parameterNames, categories, sharedParamFile });

  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await createTypeParametersGrpc(
        parameterNames,
        categories,
        sharedParamFile,
        expectedDocumentIdentity,
        options
      );
      return {
        success: grpcResult.success,
        data: grpcResult.success
          ? {
              successCount: grpcResult.successCount ?? 0,
              failureCount: grpcResult.failureCount ?? 0,
              totalCount: grpcResult.totalCount ?? parameterNames.length,
              results: grpcResult.results ?? [],
            }
          : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC fallback failed for createTypeParameters', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Parameter creation not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Add specified parameters to all families in the project
 *
 * @param parameterNames Array of parameter names to add
 * @returns Promise resolving to list of modified families
 */
export async function addParametersToAllFamilies(
  input: string[] | ParameterAdditionPayload
): Promise<
  RevitApiResponse<{
    modifiedFamiliesCount: number;
    modifiedFamilies: Array<{
      familyName: string;
      familyId: string;
      category: string;
      addedParameters: string[];
    }>;
    message: string;
  }>
> {
  const onProgress = Array.isArray(input) ? undefined : input.onProgress;
  if (isGrpcRuntimeAvailable() && onProgress) {
    try {
      const grpcPayload = JSON.stringify(input);
      const grpcResult = await addParametersToAllFamiliesGrpc(grpcPayload, { onProgress });
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          modifiedFamiliesCount: grpcResult.modifiedFamiliesCount ?? 0,
          modifiedFamilies: grpcResult.modifiedFamilies ?? [],
          message: grpcResult.message ?? 'Parameters added via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC progress path failed for addParametersToAllFamilies', error);
    }
  }

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcPayload = JSON.stringify(input);
      const grpcResult = await addParametersToAllFamiliesGrpc(grpcPayload, { onProgress });
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          modifiedFamiliesCount: grpcResult.modifiedFamiliesCount ?? 0,
          modifiedFamilies: grpcResult.modifiedFamilies ?? [],
          message: grpcResult.message ?? 'Parameters added via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC fallback failed for addParametersToAllFamilies', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Add parameters to families not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Add specified parameters as Project Parameters
 * Project parameters apply to all elements of specified categories
 *
 * @param parameterNames Array of parameter names to add
 * @param categories Array of category names (e.g., ['Pipes', 'Pipe Fittings', 'Pipe Accessories'])
 * @returns Promise resolving to operation result
 */
export async function addProjectParameters(
  parameterNames: string[],
  categories: string[],
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity,
  options?: {
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
    parameterTypeHints?: Array<{ name: string; dataType: string }>;
    bindingKind?: ParameterAdditionPayload['bindingKind'];
  }
): Promise<
  RevitApiResponse<{
    addedParametersCount: number;
    addedParameters: string[];
    categories: string[];
    message: string;
    results?: Array<{
      parameter?: string;
      success?: boolean;
      message?: string;
      error?: string;
    }>;
    undoEntries?: ConnectorProjectParameterUndoEntry[];
  }>
> {
  log('addProjectParameters called', { parameterNames, categories });

  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await addProjectParametersGrpc(
        parameterNames,
        categories,
        expectedDocumentIdentity,
        options
      );
      return {
        success: grpcResult.success,
        data: {
          addedParametersCount: grpcResult.addedParametersCount ?? 0,
          addedParameters: grpcResult.addedParameters ?? [],
          categories: grpcResult.categories ?? [],
          message: grpcResult.message ?? 'Project parameters added via gRPC',
          results: grpcResult.results,
          undoEntries: grpcResult.undoEntries,
        },
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC preferred path failed for addProjectParameters', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Add project parameters not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Load a family file (.rfa) into the Revit project
 *
 * @param familyFilePath Path to the .rfa family file
 * @returns Promise resolving to the loaded family information
 */
export async function loadFamilyIntoProject(
  familyFilePath: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<
  RevitApiResponse<{
    familyId: string;
    familyName: string;
    category: string;
    typesCount: number;
    message: string;
  }>
> {
  log('loadFamilyIntoProject called', { familyFilePath });

  if (isGrpcRuntimeAvailable() && options?.onProgress) {
    try {
      const grpcResult = await loadFamilyIntoProjectGrpc(familyFilePath, options);
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          familyId: grpcResult.familyId ?? '',
          familyName: grpcResult.familyName ?? '',
          category: grpcResult.category ?? '',
          typesCount: grpcResult.typesCount ?? 0,
          message: grpcResult.message ?? 'Family loaded via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC progress path failed for loadFamilyIntoProject', error);
    }
  }

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await loadFamilyIntoProjectGrpc(familyFilePath, options);
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          familyId: grpcResult.familyId ?? '',
          familyName: grpcResult.familyName ?? '',
          category: grpcResult.category ?? '',
          typesCount: grpcResult.typesCount ?? 0,
          message: grpcResult.message ?? 'Family loaded via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC fallback failed for loadFamilyIntoProject', error);
    }
  }

  await simulateDelay();

  // Mock response for development
  const familyName = familyFilePath.split(/[/\\]/).pop()?.replace('.rfa', '') || 'Unknown';
  return {
    success: true,
    data: {
      familyId: `mock-family-${Date.now()}`,
      familyName: familyName,
      category: 'Pipe Fittings',
      typesCount: 1,
      message: `Mock: Family "${familyName}" loaded successfully`,
    },
    message: 'Mock: Family loaded successfully',
    timestamp: new Date().toISOString(),
  };
}

export async function addParametersToSpecificFamilies(
  familyIds: string[],
  parameterNames: string[],
  includeSystemFamilies: boolean = false,
  options?: Omit<ParameterAdditionPayload, 'parameterNames'> & {
    pendingOperationRecovery?: PendingRevitOperationRecoveryMetadata;
  }
): Promise<
  RevitApiResponse<{
    modifiedFamiliesCount: number; systemFamilyParamsAdded?: number; resolvedFamilyTargetCount?: number;
    processedFamilyTargetCount?: number; openedFamilyCount?: number; unmodifiedFamilyTargetCount?: number;
    existingFamilyParameterCount?: number; conflictCount?: number; failureCount?: number;
    undoEntries?: ConnectorParameterUndoEntry[]; projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
    modifiedFamilies: Array<{
      familyName: string;
      familyId: string;
      addedParameters: string[];
    }>;
    message: string;
  }>
> {
  log('addParametersToSpecificFamilies called', { familyIds, parameterNames, includeSystemFamilies });
  const bindingKind = options?.bindingKind;
  if (bindingKind !== 'type' && bindingKind !== 'instance') {
    return {
      success: false,
      error: 'Family parameter setup requires an explicit type or instance binding kind.',
      timestamp: new Date().toISOString(),
    };
  }

  // Prefer gRPC when available so long-running family edits can stream real
  // Revit operation progress into the shared loading window.
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await addParametersToSpecificFamiliesGrpc({
        familyIds,
        parameterNames,
        includeSystemFamilies,
        categories: options?.categories,
        sharedParameterFilePath: options?.sharedParameterFilePath,
        sharedParameterFileContent: options?.sharedParameterFileContent,
        bindingKind,
        targetObjectTypes: options?.targetObjectTypes,
        objectTypeParameterName: options?.objectTypeParameterName, requireReversibleMutations: options?.requireReversibleMutations,
        expectedDocumentName: options?.expectedDocumentName,
        expectedDocumentPath: options?.expectedDocumentPath,
        parameterTypeHints: options?.parameterTypeHints,
        pendingOperationRecovery: options?.pendingOperationRecovery,
        onProgress: options?.onProgress,
      });
      return {
        success: grpcResult.success,
        data: {
          modifiedFamiliesCount: grpcResult.modifiedFamiliesCount ?? 0,
          systemFamilyParamsAdded: grpcResult.systemFamilyParamsAdded,
          resolvedFamilyTargetCount: grpcResult.resolvedFamilyTargetCount,
          processedFamilyTargetCount: grpcResult.processedFamilyTargetCount,
          openedFamilyCount: grpcResult.openedFamilyCount,
          unmodifiedFamilyTargetCount: grpcResult.unmodifiedFamilyTargetCount,
          existingFamilyParameterCount: grpcResult.existingFamilyParameterCount,
          conflictCount: grpcResult.conflictCount,
          failureCount: grpcResult.failureCount,
          undoEntries: grpcResult.undoEntries, projectUndoEntries: grpcResult.projectUndoEntries,
          modifiedFamilies: grpcResult.modifiedFamilies ?? [],
          message: grpcResult.message ?? 'Parameters added via gRPC',
        },
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC preferred path failed for addParametersToSpecificFamilies', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Add parameters to specific families not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

export async function undoAddedParametersToSpecificFamilies(
  payload: UndoAddedParametersToSpecificFamiliesPayload
): Promise<
  RevitApiResponse<{
    undoneFamiliesCount: number;
    removedParameterCount: number;
    restoredParameterCount: number;
    message: string;
  }>
> {
  log('undoAddedParametersToSpecificFamilies called', {
    entryCount: payload.entries.length,
  });

  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await undoAddedParametersToSpecificFamiliesGrpc({
        entries: payload.entries.map((entry) => ({
          familyId: entry.familyId,
          familyName: entry.familyName,
          parameterName: entry.parameterName,
          existedBefore: entry.existedBefore,
          previousBindingKind: entry.previousBindingKind,
        })),
        expectedDocumentName: payload.expectedDocumentName,
        expectedDocumentPath: payload.expectedDocumentPath,
        onProgress: payload.onProgress,
      });

      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          undoneFamiliesCount: grpcResult.undoneFamiliesCount ?? 0,
          removedParameterCount: grpcResult.removedParameterCount ?? 0,
          restoredParameterCount: grpcResult.restoredParameterCount ?? 0,
          message: grpcResult.message ?? 'Undo completed via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC preferred path failed for undoAddedParametersToSpecificFamilies', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Undo added parameters to specific families is unavailable in development mode',
    timestamp: new Date().toISOString(),
  };
}

export async function undoAddedProjectParameters(
  payload: UndoAddedProjectParametersPayload
): Promise<
  RevitApiResponse<{
    removedParameterCount: number; restoredParameterCount: number; message: string;
  }>
> {
  log('undoAddedProjectParameters called', {
    parameterCount: payload.parameterNames.length,
  });

  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await undoAddedProjectParametersGrpc(payload.parameterNames, {
        expectedDocumentName: payload.expectedDocumentName,
        expectedDocumentPath: payload.expectedDocumentPath,
      }, { onProgress: payload.onProgress, entries: payload.entries });
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          removedParameterCount: grpcResult.removedParameterCount ?? 0, restoredParameterCount: grpcResult.restoredParameterCount ?? 0,
          message: grpcResult.message ?? 'Project parameter undo completed via gRPC',
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC preferred path failed for undoAddedProjectParameters', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Undo added project parameters is unavailable in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Delete old parameter and add new parameter (WARNING: data will be lost)
 *
 * @param oldParamName Name of parameter to delete
 * @param newParamName Name of parameter to add
 * @param categories Array of category names
 * @returns Promise resolving to operation results with warnings
 */
export async function deleteAndReAddParameter(
  oldParamName: string,
  newParamName: string,
  categories: string[],
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<
  RevitApiResponse<{
    oldParameter: string;
    newParameter: string;
    categories: string[];
    warnings: string[];
  }>
> {
  log('deleteAndReAddParameter called', { oldParamName, newParamName, categories });

  if (isGrpcRuntimeAvailable() && options?.onProgress) {
    try {
      const grpcResult = await deleteAndReAddParameterGrpc(
        oldParamName,
        newParamName,
        categories,
        expectedDocumentIdentity,
        options
      );
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          oldParameter: grpcResult.oldParameter ?? oldParamName,
          newParameter: grpcResult.newParameter ?? newParamName,
          categories: grpcResult.categories ?? categories,
          warnings: grpcResult.warnings ?? [],
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC progress path failed for deleteAndReAddParameter', error);
    }
  }

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await deleteAndReAddParameterGrpc(
        oldParamName,
        newParamName,
        categories,
        expectedDocumentIdentity,
        options
      );
      return {
        success: grpcResult.success,
        data: grpcResult.success ? {
          oldParameter: grpcResult.oldParameter ?? oldParamName,
          newParameter: grpcResult.newParameter ?? newParamName,
          categories: grpcResult.categories ?? categories,
          warnings: grpcResult.warnings ?? [],
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC fallback failed for deleteAndReAddParameter', error);
    }
  }

  await simulateDelay();

  return {
    success: false,
    error: 'Mock: Delete and re-add parameter not available in development mode',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get all families with their parameters for QuickFill display
 *
 * @returns Promise resolving to families with their parameters
 */
export async function getFamiliesWithParameters(): Promise<
  RevitApiResponse<{
    familyCount: number;
    families: FamilyWithParameters[];
  }>
> {
  log('getFamiliesWithParameters called');

  // gRPC fallback
  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await getFamiliesWithParametersGrpc();
      if (grpcResult.success && grpcResult.families) {
        return {
          success: true,
          data: {
            familyCount: grpcResult.families.length,
            families: grpcResult.families.map((f) => ({
              id: f.id,
              name: f.name,
            category: f.category as RevitElementCategory,
              isEditable: f.isEditable,
              types: f.types.map((t) => ({
                id: t.id,
                name: t.name,
                parameters: t.parameterValues.map((pv) => ({
                  name: pv.parameterName,
                  value: pv.value || '',
                  dataType: normalizeRevitDataType(pv.dataType),
                  isShared: false,
                  isInstance: true,
                })),
              })),
            })),
          },
          timestamp: new Date().toISOString(),
        };
      }
      return {
        success: false,
        error: grpcResult.error || 'gRPC getFamiliesWithParameters failed',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC fallback failed for getFamiliesWithParameters', error);
    }
  }

  await simulateDelay();

  return {
    success: true,
    data: {
      familyCount: MOCK_REVIT_FAMILIES.length,
      families: MOCK_REVIT_FAMILIES.map((f) => ({
        id: f.id,
        name: f.name,
        category: f.category,
        isEditable: true,
        types: f.types.map((t) => ({
          id: t.id,
          name: t.name,
          parameters:
            t.parameterValues?.map((pv) => ({
              name: pv.parameterName,
              value: pv.value || '',
              dataType: 'string' as RevitDataType,
              isShared: false,
              isInstance: true,
            })) || [],
        })),
      })),
    },
    message: 'Mock data - families with parameters retrieved successfully',
    timestamp: new Date().toISOString(),
  };
}

// =============================================================================
// Create Parameters from Parsed Shared Parameter File
// =============================================================================

/**
 * Input for creating parameters from parsed shared parameter data
 */
export interface CreateParametersFromParsedInput extends ExpectedRevitDocumentIdentity {
  /** Parsed parameters to create */
  parameters: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }>;
  /** Categories to apply parameters to */
  categories: string[];
  /** Whether these are type or instance parameters */
  isType: boolean;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

/**
 * Create Revit parameters from parsed shared parameter file data
 *
 * This function takes parameters that have been parsed from a shared parameter file
 * (either the default PipeEX file or a user-provided file) and creates them in Revit.
 *
 * @param input The parsed parameters and configuration
 * @returns Promise resolving to creation results
 */
export async function createParametersFromParsedFile(
  input: CreateParametersFromParsedInput
): Promise<
  RevitApiResponse<{
    successCount: number; failureCount: number; totalCount: number;
    results: ParameterCreationResult[]; undoEntries?: ConnectorProjectParameterUndoEntry[];
  }>
> {
  log('createParametersFromParsedFile called', {
    parameterCount: input.parameters.length,
    categories: input.categories,
    isType: input.isType,
  });

  if (isGrpcRuntimeAvailable()) {
    try {
      const grpcResult = await createParametersFromParsedFileGrpc(
        input.parameters.map((p) => ({
          name: p.name,
          guid: p.guid,
          dataType: p.dataType,
          groupId: p.groupId,
          description: p.description ?? '',
        })),
        input.categories,
        input.isType,
        {
          expectedDocumentName: input.expectedDocumentName,
          expectedDocumentPath: input.expectedDocumentPath,
        },
        { onProgress: input.onProgress }
      );
      // Per-item results must survive a failed batch: the caller needs them
      // to name which parameters conflicted or failed, and to reclassify
      // tolerated binding conflicts. Only a response with no results at all
      // (document mismatch, empty request) maps to missing data.
      const hostResults = grpcResult.results ?? [];
      return {
        success: grpcResult.success,
        data: grpcResult.success || hostResults.length > 0 ? {
          successCount: grpcResult.successCount ?? 0,
          failureCount: grpcResult.failureCount ?? 0,
          totalCount: grpcResult.totalCount ?? 0,
          results: hostResults.map((r) => ({
            parameter: r.parameter,
            status: r.status,
            success: r.success,
            message: r.message,
            error: r.error,
            guid: r.guid,
            categories: r.categories,
          })),
          undoEntries: grpcResult.undoEntries,
        } : undefined,
        error: grpcResult.error,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      log('gRPC preferred path failed for createParametersFromParsedFile', error);
      // With a live gRPC runtime a thrown transport error must block, never
      // fall through to the development mock that fabricates success.
      return {
        success: false,
        error: error instanceof Error
          ? error.message
          : 'Failed to create parameters from parsed file',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Mock for development
  await simulateDelay();

  const results: ParameterCreationResult[] = input.parameters.map((param) => ({
    parameter: param.name,
    success: true,
    message: `Mock: Created parameter ${param.name}`,
    guid: param.guid,
    categories: input.categories,
  }));

  return {
    success: true,
    data: {
      successCount: results.length,
      failureCount: 0,
      totalCount: results.length,
      results,
    },
    message: 'Mock: Parameters created successfully',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Load and parse shared parameter file content (for browser environment)
 *
 * In a browser context, we can't read files directly. This function is used
 * when the user uploads a file through a file input element.
 *
 * @param fileContent The content of the shared parameter file as a string
 * @returns Promise resolving to parsed parameters
 */
export async function parseSharedParameterFileContent(
  fileContent: string
): Promise<
  RevitApiResponse<{
    parameters: SharedParameterDefinition[];
    groups: Array<{ id: number; name: string }>;
  }>
> {
  log('parseSharedParameterFileContent called', {
    contentLength: fileContent.length,
  });

  // This parsing happens client-side using the sharedParameterParser service
  // We just validate the format here
  try {
    // Import the parser dynamically to avoid circular dependencies
    const { parseSharedParameterFile, validateSharedParameterFile } = await import(
      '../../services/sharedParameterParser'
    );

    const validation = validateSharedParameterFile(fileContent);
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error || 'Invalid file format',
        timestamp: new Date().toISOString(),
      };
    }

    const parsed = parseSharedParameterFile(fileContent);

    const parameters: SharedParameterDefinition[] = parsed.parameters.map((p) => ({
      name: p.name,
      guid: p.guid,
      group: p.groupName || `Group ${p.groupId}`,
      dataType: p.dataType as RevitDataType,
      parameterType: 'Text', // Default, actual type is in dataType
      description: p.description,
      visible: p.visible,
    }));

    return {
      success: true,
      data: {
        parameters,
        groups: parsed.groups,
      },
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to parse file',
      timestamp: new Date().toISOString(),
    };
  }
}

// =============================================================================
// Event Listeners (mock, in-process)
// =============================================================================

type RevitEventType =
  | 'selectionChanged'
  | 'documentChanged'
  | 'viewChanged'
  | 'parametersUpdated';

type RevitEventHandler = (data: unknown) => void;

const eventHandlers: Map<RevitEventType, Set<RevitEventHandler>> = new Map();

/**
 * Subscribe to Revit events (in-process mock event bus)
 */
export function subscribeToRevitEvent(
  eventType: RevitEventType,
  handler: RevitEventHandler
): () => void {
  log('subscribeToRevitEvent', { eventType });

  if (!eventHandlers.has(eventType)) {
    eventHandlers.set(eventType, new Set());
  }

  eventHandlers.get(eventType)!.add(handler);

  // Return unsubscribe function
  return () => {
    eventHandlers.get(eventType)?.delete(handler);
  };
}

/**
 * Emit a mock event (for testing purposes)
 */
export function emitMockEvent(eventType: RevitEventType, data: unknown): void {
  log('emitMockEvent', { eventType, data });

  const handlers = eventHandlers.get(eventType);
  if (handlers) {
    handlers.forEach((handler) => handler(data));
  }
}

// =============================================================================
// Export Default Bridge Object
// =============================================================================

/**
 * Revit Bridge API
 *
 * Central access point for all Revit API interactions.
 */
export const revitBridge = {
  // Configuration
  configure: configureRevitBridge,

  // Parameters
  getParameters,
  getParametersByCategory,
  updateParameterValue,
  updateFamilyTypeParameter,

  // Elements
  getElements,
  getSelectedElements,
  selectElements,
  highlightElements,

  // Families
  getFamilies,
  getFamiliesWithParameters,

  // Shared Parameters
  getSharedParameters,

  // Settings
  getSettings,
  updateSettings,

  // Commands
  executeCommand,
  refreshParameters,
  exportPCF,
  validateParameters,
  applyMappings,

  // Shared Parameter Management
  loadSharedParameterFile,
  createTypeParameters,
  addParametersToAllFamilies,
  addParametersToSpecificFamilies,
  undoAddedParametersToSpecificFamilies,
  undoAddedProjectParameters,
  loadFamilyIntoProject,
  addProjectParameters,
  deleteAndReAddParameter,
  createParametersFromParsedFile,
  parseSharedParameterFileContent,

  // Events
  subscribeToRevitEvent,
  emitMockEvent,
};

export default revitBridge;
